import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import Slider from 'rc-slider'
import debounce from 'lodash/debounce'

import { formatPrice } from '../../../../components/utils/pricing'
import styledTheme from '../../../../styles/theme'
import typeStyle from '../../../../styles/typeStyle'

/* Half a handle width each side keeps the end handles inside the box */
const RangeWrapper = styled.div`
  padding: 0 10px;
  margin-bottom: 20px;
`

const LabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`

const Label = styled.div`
  ${typeStyle('-ds-font-body-regular')}
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
`

// No marginLeft: rc-slider v10 centres handles with translateX(-50%),
// so the legacy -10 shifted both handles off the true rail positions
const handleStyle = {
  borderColor: styledTheme.brand.jungle,
  height: 20,
  width: 20,
  marginTop: -8,
  backgroundColor: 'white',
  opacity: 1,
}

type TransformCurve = 'logarithmic' | 'linear'

const transformIn = (value: number, curve: TransformCurve) => {
  if (value === 0) {
    return 0
  }

  if (curve === 'logarithmic') {
    return Math.log(value)
  }

  return value
}

const transformOut = (value: number, curve: TransformCurve) => {
  if (value === 0) {
    return 0
  }

  if (curve === 'logarithmic') {
    return Math.exp(value)
  }

  return value
}

type PriceRangeProps = {
  initialPriceStart: number
  initialPriceEnd: number
  minPrice: number
  maxPrice: number
  onChange: (_: [number, number]) => any
  transformCurve?: TransformCurve
}

type Value = [number, number]

export const BasePriceRange = ({
  onChange,
  minPrice,
  maxPrice,
  initialPriceStart,
  initialPriceEnd,
  transformCurve = 'logarithmic',
}: PriceRangeProps) => {
  const [value, setValue] = useState([minPrice, maxPrice])

  useEffect(() => {
    setValue([initialPriceStart, initialPriceEnd])
  }, [initialPriceStart, initialPriceEnd])

  const debouncedOnChange = useMemo(() => debounce(onChange, 200), [onChange])

  const handleOnAfterChange = ([start, end]: Value) => {
    const nextStart = transformOut(start, transformCurve)
    const nextEnd = transformOut(end, transformCurve)

    if (nextStart != null && nextEnd != null) {
      debouncedOnChange([Math.round(nextStart), Math.round(nextEnd)])
    }
  }

  const handleChange = ([start, end]: Value) => {
    const nextStart = transformOut(start, transformCurve)
    const nextEnd = transformOut(end, transformCurve)

    setValue([nextStart, nextEnd])
  }

  return (
    <div>
      <LabelWrapper>
        <Label>{formatPrice(value[0], 0)}</Label>
        <Label>{formatPrice(value[1], 0)}</Label>
      </LabelWrapper>

      {minPrice != null && maxPrice != null && (
        <RangeWrapper>
          <Slider
            range
            trackStyle={[
              {
                backgroundColor: styledTheme?.['-ds-core-400']?.value,
                borderRadius: 0,
              },
            ]}
            railStyle={{
              backgroundColor: styledTheme?.['-ds-neutral-300']?.value,
            }}
            handleStyle={[handleStyle, handleStyle]}
            min={transformIn(minPrice, transformCurve)}
            max={transformIn(maxPrice, transformCurve)}
            step={
              (transformIn(maxPrice, transformCurve) -
                transformIn(minPrice, transformCurve)) /
              1000
            }
            value={[
              transformIn(value[0], transformCurve),
              transformIn(value[1], transformCurve),
            ]}
            onChange={handleChange}
            onAfterChange={handleOnAfterChange}
          />
        </RangeWrapper>
      )}
    </div>
  )
}

export default BasePriceRange
